import { type KeyboardEvent, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Divider, VFlex } from '@/components/base';
import ScrollView from '@/components/Common/ScrollView';
import Dropdown, { type DropdownProps } from '@/components/Dropdown/Dropdown';
import Input from '@/components/Inputs/Input';
import { Option } from '@/components/Select/styled';
import type { SelectOptionT } from '@/components/Select/types';
import { UNASSIGNED_LABEL } from '@/configs/reserved-word';
import useAgentStore from '@/features/Agent/stores';
import { useInputs } from '@/hooks/pure/useInputs';
import useKeymap from '@/hooks/pure/useKeymap';
import { lowerCaseMatch } from '@/utils/util';

import ActionMenuFooter from './ActionMenuFooter';

type CollectiveAgentSelectDropdownMenuProps = {
  selectedAgent: string;
  onOptionSelect: (key: string) => void;
  updateAgents: () => void;
  dropdownProps: DropdownProps;
};

const CollectiveAgentSelectDropdownMenu = ({
  selectedAgent,
  onOptionSelect,
  updateAgents,
  dropdownProps,
}: CollectiveAgentSelectDropdownMenuProps) => {
  const agentMap = useAgentStore((state) => state.agentMap);
  const [state, onChange] = useInputs({
    searchValue: '',
  });

  const options: SelectOptionT[] = useMemo(() => {
    return [
      // Put 'Unassigned' option at the top of the list
      {
        value: UNASSIGNED_LABEL,
        searchName: '',
      },
      ...Object.values(agentMap).map((agent) => ({
        value: agent.alias,
        searchName: agent.name,
      })),
    ];
  }, [agentMap]);

  const [focused, setFocused] = useState<SelectOptionT | null>(
    options.find((option) => option.value === selectedAgent) || options[0],
  );

  useEffect(() => {
    setFocused(
      options.find((option) => option.value === selectedAgent) || options[0],
    );
  }, [options, selectedAgent]);

  useEffect(() => {
    const currList = options.filter((option) => {
      return lowerCaseMatch(option.searchName, state.searchValue);
    });
    const curr = currList.findIndex(
      (option) => option.value === focused?.value,
    );
    if (curr === -1 && currList.length) {
      setFocused(currList[0]);
    } else if (currList.length === 0) {
      setFocused(null);
    }
  }, [focused?.value, options, state.searchValue]);

  const focusNextItem = () => {
    const currList = options.filter((option) => {
      return lowerCaseMatch(option.searchName, state.searchValue);
    });
    const curr = currList.findIndex(
      (option) => option.value === focused?.value,
    );
    if (curr === currList.length - 1) return;
    setFocused(currList[curr + 1]);
  };

  const focusPrevItem = () => {
    const currList = options.filter((option) => {
      return lowerCaseMatch(option.searchName, state.searchValue);
    });
    const curr = currList.findIndex(
      (option) => option.value === focused?.value,
    );
    if (curr === 0) return;
    setFocused(currList[curr - 1]);
  };

  useKeymap('ArrowUp', focusPrevItem);
  useKeymap('ArrowDown', focusNextItem);
  useKeymap('Enter', () => {
    if (!focused) return;
    onOptionSelect(focused.value as string);
  });

  const filteredOptions = useMemo(() => {
    return options.filter((option) => {
      return lowerCaseMatch(option.searchName, state.searchValue);
    });
  }, [options, state.searchValue]);

  const onSearchInputKeyDown = (e: KeyboardEvent) => {
    e.stopPropagation();
    if (e.code === 'ArrowUp') {
      focusPrevItem();
    }
    if (e.code === 'ArrowDown') {
      focusNextItem();
    }
    if (e.code === 'Enter') {
      if (!focused) return;
      onOptionSelect(focused.value as string);
    }
    if (e.code === 'Escape') {
      dropdownProps.close?.();
    }
  };

  const handleCancelButtonClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    dropdownProps.close?.();
  };

  const handleConfirmButtonClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    updateAgents();
    dropdownProps.close?.();
  };

  return (
    <Dropdown {...dropdownProps}>
      <Style
        style={{
          width: dropdownProps.modalWidth,
        }}
      >
        <Input
          initFocus
          sizeType="l"
          placeholder="Search..."
          name="searchValue"
          hideBorder
          value={state.searchValue}
          onChange={onChange}
          onKeyDown={onSearchInputKeyDown}
        />
        <Divider direction="h" />
        <ScrollView style={{ maxHeight: 400 }}>
          <VFlex g={4}>
            {filteredOptions.map((option) => (
              <Option
                focused={focused?.value === option.value}
                key={option.value}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onOptionSelect(option.value as string);
                }}
              >
                {option.searchName}
              </Option>
            ))}
          </VFlex>
        </ScrollView>
        <Divider direction="h" />
        <ActionMenuFooter
          handleCancelButtonClick={handleCancelButtonClick}
          handleConfirmButtonClick={handleConfirmButtonClick}
        />
      </Style>
    </Dropdown>
  );
};

const Style = styled(VFlex)`
  padding: 4px;
`;

export default CollectiveAgentSelectDropdownMenu;
